import { motion, useTransform, MotionValue } from "motion/react";
import { ResultOf } from "gql.tada";
import {
  documentToReactComponents,
  Options,
} from "@contentful/rich-text-react-renderer";
import {
  BLOCKS,
  MARKS,
  Document as RichTextDocument,
} from "@contentful/rich-text-types";
import { RichText } from "@components/shared/core/RichText";
import { isValidPalette } from "../../../types/ThemeModel";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { ThemeContextProvider } from "@components/providers/ThemeContextProvider";
import { IntroBenefitsSectionById } from "@queries/entries/IntroBenefitsSectionById";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { CtaButton } from "@components/entries/shared/CtaButton";
import { Analytics } from "@utils/Analytics";

const ALLOWABLE_OFFSET_MOBILE = 150;
const ALLOWABLE_OFFSET_DESKTOP = 150;

export const AnimatedResponsiveDetails = ({
  benefitItem,
  scrollYProgress,
  index,
  total,
}: {
  scrollYProgress: MotionValue<number>;
  index: number;
  total: number;
  benefitItem: NonNullable<
    NonNullable<
      ResultOf<typeof IntroBenefitsSectionById>["introBenefitsSection"]
    >["benefitItemsCollection"]
  >["items"][number];
}) => {
  const palette = benefitItem?.themeColor?.palette;
  const mappedProgress = useTransform(
    scrollYProgress,
    (value) => value * (total + 1)
  );

  const isDesktop = useIsDesktop();
  
  const isWideButNotTall = typeof window !== 'undefined' && 
    (window.innerWidth / window.innerHeight) > 1.8;
  
  const ALLOWABLE_OFFSET = isDesktop
    ? ALLOWABLE_OFFSET_DESKTOP
    : ALLOWABLE_OFFSET_MOBILE;

  const handleCtaClick = () => {
    if (benefitItem?.ctaEvent && typeof benefitItem.ctaEvent === 'string') {
      Analytics.trackEvent(`${benefitItem.ctaEvent}`);
    }
  };

  const splitLinesValue = useTransform(
    mappedProgress,
    [0, index, index + 0.5, index + 1, index + 2],
    [-1, -1, 0, 0, 1]
  );

  const factSourceOpacity = useTransform(
    mappedProgress,
    [index, index + 0.5, index + 1, index + 1.5, index + 2],
    [0, 0, 0.5, 0, 0]
  );

  const ctaTranslateY = useTransform(
    splitLinesValue,
    [-1, 0, 1],
    ['300%', '0%', '-300%']
  );

  const ctaOpacity = useTransform(
    splitLinesValue,
    [-1, 0, 1],
    [0, 1, 0]
  );

  const slideZIndex = useTransform(
    splitLinesValue,
    [-1, -0.5, 0.5, 1],
    [0, 1, 1, 0]
  );

  if (!isValidPalette(palette)) return null;

  return (
    <motion.div
      className="absolute left-0 top-1/2 max-w-full grid-container w-full"
      style={{
        transform: `translateY(calc(-50% + ${
          ALLOWABLE_OFFSET * (index / (total - 1) - ((isWideButNotTall && benefitItem?.ctaText) ? 0.65 : 0.25))
        }px))`,
        zIndex: slideZIndex,
      }}
    >
      {!!benefitItem?.factSource && (
        <motion.div
          className="absolute left-0 bottom-[-32px] dsk:col-span-17 col-span-10 dsk:col-start-2 col-start-1"
          style={{
            opacity: factSourceOpacity,
          }}
        >
          <RichText content={benefitItem.factSource} />
        </motion.div>
      )}
      <ThemeContextProvider
        className="dsk:col-start-2 dsk:col-span-17 col-span-10"
        theme={{
          palette: palette,
          background: "neutral-light",
          foreground: "highlight",
        }}
      >
        <p className="text-left dsk:text-heading-2 text-heading-3">
          <AnimatedSplitLines value={splitLinesValue}>
            {documentToReactComponents(
              (benefitItem?.info?.json ?? {}) as RichTextDocument,
              highlightedTextRenderOptions
            )}
          </AnimatedSplitLines>
        </p>
        {(benefitItem?.ctaText && benefitItem?.ctaLink && 
          typeof benefitItem.ctaText === 'string' && 
          typeof benefitItem.ctaLink === 'string') ? (
          <motion.div
            className="mt-6 overflow-hidden py-2 -my-2 relative z-10"
            style={{
              opacity: ctaOpacity,
            }}
          >
            <motion.div
              style={{
                y: ctaTranslateY,
              }}
            >
              <motion.div
                whileHover={{ x: 5 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <CtaButton 
                  ctaText={benefitItem.ctaText} 
                  ctaLink={benefitItem.ctaLink}
                  variant="light-on-blur"
                  onClick={handleCtaClick}
                />
              </motion.div>
            </motion.div>
          </motion.div>
        ) : null}
      </ThemeContextProvider>
    </motion.div>
  );
};

const highlightedTextRenderOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <>
        <span className="text-brand-cream">{children}</span>
        <br />
      </>
    ),
  },
  renderMark: {
    [MARKS.ITALIC]: (text) => (
      <span className="text-palette-current-highlight">{text}</span>
    ),
  },
};
